import { Flex, Text, Grid, GridItem, Button } from '@chakra-ui/react'
import { ChakraPrimaryButton } from 'components/Button'
import { utcToZonedTime, format, zonedTimeToUtc } from 'date-fns-tz'
import { LabLocation } from 'hooks/useLabScheduling/useLabscheduling'
import { useEffect, useRef, useState } from 'react'
import {
  LabScheduleAvailabilityMoreTimesClickProps,
  LabSchedulingFilter,
  LabProvider,
} from 'utils/amplitude.types'
import { Amplitude } from 'utils/amplitude.utils'

type SelectedSlotData = {
  selectedLabId: string
  selectedTime: string
}

type Metadata = {
  labAppointmentId: string
  isReschedule: boolean
  labProvider: LabProvider
}

type TimeSlotCardProps = {
  labLocation: LabLocation
  selectedSlotData: SelectedSlotData
  selectSlotHandler: (
    id: string,
    address: string,
    time: string,
    timezone: string
  ) => void
  bookHandler: () => void
  setFocus: () => void
  isFocused: boolean
  amplitudeLabSchedulingMetadata: Metadata
  selectedTimezone: string
  originalTimezone: string
  selectedDate: string
  filterApplied: LabSchedulingFilter
  isMobile: boolean
}

const TimeSlotCard = ({
  labLocation,
  selectedSlotData,
  selectedDate,
  filterApplied,
  selectSlotHandler,
  bookHandler,
  setFocus,
  isFocused,
  amplitudeLabSchedulingMetadata,
  selectedTimezone,
  originalTimezone,
  isMobile,
}: TimeSlotCardProps) => {
  const [showAllSlots, setShowAllSlots] = useState(false)
  const MAX_VISIBLE_SLOTS = 6

  const {
    id,
    address: { address1, address2, city, state, zipcode },
    slots,
    distance,
  } = labLocation

  // Maybe pull this into a util function or something
  const formattedAddress = `${address1} ${address2}, ${city}, ${state}, ${zipcode}`
  const { selectedLabId, selectedTime } = selectedSlotData

  const visibleSlots = showAllSlots ? slots : slots.slice(0, MAX_VISIBLE_SLOTS)
  const hasMoreSlots = slots.length > MAX_VISIBLE_SLOTS

  const toggleShowMore = () => {
    // Only send event if user wants to see more slots
    if (!showAllSlots) {
      const {
        labAppointmentId,
        isReschedule,
        labProvider,
      } = amplitudeLabSchedulingMetadata
      const amplitudeData: LabScheduleAvailabilityMoreTimesClickProps = {
        slots_date: selectedDate,
        lab_location: formattedAddress,
        lab_code: id,
        available_slots_times: slots.map((slot) => slot.time),
        available_slots_cnt: slots.length,
        filter_applied: filterApplied,
        is_reschedule: isReschedule,
        lab_provider: labProvider,
        lab_appointment_id: labAppointmentId,
      }

      Amplitude.labScheduleAvailabilityMoreTimesClick(amplitudeData)
    }

    setShowAllSlots(!showAllSlots)
  }

  const onClickHandler = (
    id: string,
    formattedAddress: string,
    time: string,
    timezone: string
  ) => {
    selectSlotHandler(id, formattedAddress, time, timezone)
    setFocus()
  }

  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isFocused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [isFocused])

  const displayTime = (slotDate: string, slotTime: string) => {
    const time = `${slotDate}T${slotTime}:00`
    const utcDate = zonedTimeToUtc(time, originalTimezone)

    const zonedDate = utcToZonedTime(utcDate, selectedTimezone)
    return format(zonedDate, 'hh:mm aaaa', { timeZone: selectedTimezone })
  }

  return (
    <Flex
      flexDirection="column"
      borderRadius=".25rem"
      border="1px solid var(--latest-august-22-grey-scale-200, #CDCEDF);"
      padding="1rem"
      width={isMobile ? '100%' : '360px'}
      ref={cardRef}
    >
      <Flex justifyContent="space-between">
        <Text fontWeight="500" fontSize="1rem">
          {formattedAddress}
        </Text>
        <Text color="#4678E6" fontSize=".75rem" whiteSpace="nowrap">
          {distance} MI
        </Text>
      </Flex>
      <Grid templateColumns="repeat(3, 1fr)" gap=".5rem" marginBottom=".75rem">
        {visibleSlots.map((slot) => {
          const isSelected = selectedLabId === id && slot.time === selectedTime
          return (
            <GridItem
              key={`${id}-${slot.time}`}
              border="1px solid var(--latest-august-22-grey-scale-200, #CDCEDF);"
              borderRadius=".25rem"
              textAlign="center"
              fontSize=".875rem"
              fontWeight="500"
              cursor="pointer"
              color={isSelected ? '#FFFFFF' : '#141743'}
              backgroundColor={isSelected ? '#141743' : '#FFFFFF'}
              onClick={() =>
                onClickHandler(
                  id,
                  formattedAddress,
                  slot.time,
                  labLocation.address.timezone
                )
              }
            >
              {displayTime(slot.date, slot.time)}
            </GridItem>
          )
        })}
      </Grid>
      {hasMoreSlots && (
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleShowMore}
          marginBottom=".75rem"
          color="#4678E6"
          fontSize=".75rem"
        >
          {showAllSlots
            ? 'Show Less'
            : `Show ${slots.length - MAX_VISIBLE_SLOTS} More`}
        </Button>
      )}
      <ChakraPrimaryButton
        width="100%"
        onClick={() => bookHandler()}
        disabled={selectedLabId !== id}
      >
        Book
      </ChakraPrimaryButton>
    </Flex>
  )
}

export default TimeSlotCard
